import React, { useState, useEffect, useContext } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllMiscBills, deleteMiscBill } from "../../api/Api";
import EditMiscBill from "./EditMiscBill";
import AddMiscBill from "./AddMiscBill";
import MiscBillView from "./MiscBillView";
import { FaEye, FaPlus, FaEdit } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import { Filter } from "lucide-react";
import { AuthContext } from "../../utils/AuthContext";
import { generateAcademicYears } from "../../utils/academicYearUtils";
const MiscBillList = () => {
  const [miscBills, setMiscBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBill, setEditingBill] = useState(null);
  const [showAddBillForm, setShowAddBillForm] = useState(false);
  const [viewingBill, setViewingBill] = useState(null);

  // Filters and Pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { academicYear, setAcademicYear } = useContext(AuthContext);

const academicYears = generateAcademicYears();

  useEffect(() => {
    if (!showAddBillForm && !editingBill && !viewingBill) {
      fetchMiscBills();
    }
  }, [showAddBillForm, editingBill, viewingBill]);

  // Reset pagination when search or dates change
  useEffect(() => {
    setCurrentPage(1);
}, [searchTerm, startDate, endDate, academicYear]);

  const fetchMiscBills = async () => {
    try {
      setLoading(true);
      const response = await getAllMiscBills();
      if (response && Array.isArray(response.data)) {
        setMiscBills(response.data);
      } else {
        setMiscBills([]);
      }
    } catch (err) {
      console.error("Error fetching miscellaneous bills:", err);
      toast.error("Failed to load miscellaneous bills.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBill = async (id) => {
    if (window.confirm("Are you sure you want to delete this bill?")) {
      try {
        await deleteMiscBill(id);
        toast.success("Bill deleted successfully!");
        fetchMiscBills();
      } catch (err) {
        console.error("Error deleting bill:", err);
        toast.error("Failed to delete bill.");
      }
    }
  };

  const handleEditClick = (bill) => {
    setEditingBill(bill);
  };

  const handleCloseEdit = () => {
    setEditingBill(null);
  };

  const handleBillUpdated = () => {
    fetchMiscBills();
  };

  const handleViewClick = (bill) => {
    setViewingBill(bill);
  };

  // --- Filtering Logic ---
  const filteredBills = miscBills.filter((bill) => {
    const studentName = bill.studentId?.studentName?.toLowerCase() || "";
    const admissionNo = bill.studentId?.admissionNo?.toLowerCase() || "";
    const invoiceNo = bill.invoiceNo?.toLowerCase() || "";
    const searchLower = searchTerm.toLowerCase();
const matchesAcademicYear =
  !academicYear ||
  bill.academicYear === academicYear;
    const matchesSearch =
      studentName.includes(searchLower) ||
      admissionNo.includes(searchLower) ||
      invoiceNo.includes(searchLower) ||
      bill._id?.toLowerCase().includes(searchLower);

    let matchesDate = true;
    if (startDate && endDate) {
        const billDate = new Date(bill.createdAt || bill.date);
        const start = new Date(startDate);
        const end = new Date(endDate);
        // Include the end date fully
        end.setHours(23, 59, 59, 999);
        matchesDate = billDate >= start && billDate <= end;
    }
return (
  matchesSearch &&
  matchesDate &&
  matchesAcademicYear
);
  });

  // --- Pagination Logic ---
  const totalPages = Math.ceil(filteredBills.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBills.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return <div className="p-6 text-center">Loading miscellaneous bills...</div>;
  }

  if (editingBill) {
    return (
      <EditMiscBill
        bill={editingBill}
        onBack={handleCloseEdit}
        onBillUpdated={handleBillUpdated}
      />
    );
  }

  if (showAddBillForm) {
    return <AddMiscBill onBack={() => setShowAddBillForm(false)} />;
  }

  if (viewingBill) {
    return <MiscBillView bill={viewingBill} onBack={() => setViewingBill(null)} />;
  }

  return (
    <div className="p-6 max-w-screen-xl mx-auto bg-white min-h-screen font-sans">
      <ToastContainer />

{/* Header Section */}
<div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 mb-6">

  <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">

    {/* Left Section */}
    <div className="flex flex-wrap items-center gap-2">

      {/* Title */}
      <div className="min-w-fit">
        <h2 className="text-2xl font-bold text-[#16bab1] leading-tight">
          Cash Receipt
        </h2>

        <p className="text-sm text-gray-500">
          {filteredBills.length} Bills Found
        </p>
      </div>

      {/* Academic Year Filter */}
      <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200 min-w-[180px]">

        <Filter size={16} className="text-[#16bab1]" />

        <div className="flex flex-col w-full">

          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Academic Year
          </label>

          <select
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            className="bg-transparent text-sm font-semibold text-gray-700 outline-none cursor-pointer"
          >
            <option value="">Overall View</option>

            {academicYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}

          </select>

        </div>

      </div>

      {/* Search */}
      <div className="relative w-full sm:w-[250px]">

        <IoSearchOutline
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          size={18}
        />

        <input
          type="text"
          placeholder="Search bills..."
          className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-[#16bab1]"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Date Filter */}
      <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">

        <div className="flex flex-col">
          <label className="text-[10px] font-bold uppercase text-gray-400">
            From
          </label>

          <input
            type="date"
            className="bg-transparent outline-none text-sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="w-px h-8 bg-gray-300"></div>

        <div className="flex flex-col">
          <label className="text-[10px] font-bold uppercase text-gray-400">
            To
          </label>

          <input
            type="date"
            className="bg-transparent outline-none text-sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

      </div>
   <button
        onClick={() => setShowAddBillForm(true)}
        className="bg-[#16bab1] hover:bg-[#139a93] transition-all duration-200 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 shadow-sm font-semibold whitespace-nowrap"
      >
        <FaPlus size={14} />
        Add Bill
      </button>
    </div>

    {/* Right Side */}
    <div className="flex justify-end">

   

    </div>

  </div>

</div>

      {/* Table Section */}
      <div className=" shadow-sm min-w-full border border-gray-200">
        <table className="min-w-full bg-white">
          <thead className="bg-[#16bab1] text-white">
            <tr>
              <th className="py-2 px-2 text-center text-sm font-medium border-r border-[#139a93]">
                S.No
              </th>
              <th className="py-2 px-2 text-left text-sm font-medium border-r border-[#139a93]">
                Admission No
              </th>
              <th className="py-2 px-2 text-left text-sm font-medium border-r border-[#139a93]">
                Student Name
              </th>
              <th className="py-2 px-2 text-left text-sm font-medium border-r border-[#139a93]">
                Class
              </th>
              <th className="py-2 px-2 text-left text-sm font-medium border-r border-[#139a93]">
                Year
              </th>
              <th className="py-2 px-2 text-left text-sm font-medium border-r border-[#139a93]">
                CR.No
              </th>
              <th className="py-2 px-2 text-left text-sm font-medium border-r border-[#139a93]">
                Paid
              </th>
              <th className="py-2 px-2 text-left text-sm font-medium border-r border-[#139a93]">
                Remaining
              </th>
              <th className="py-2 px-2 text-left text-sm font-medium border-r border-[#139a93]">
                Paid Mode
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium border-r border-[#139a93]">
                Date
              </th>
              <th className="py-2 px-2 text-center text-sm font-medium">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentItems.length > 0 ? (
              currentItems.map((bill, index) => {
                const rowNumber = indexOfFirstItem + index + 1;
                const dateObj = new Date(bill.createdAt || bill.date); // Fallback to date if createdAt missing
                const formattedDate = !isNaN(dateObj)
                  ? dateObj.toLocaleDateString("en-GB")
                  : "N/A"; // DD-MM-YYYY

                return (
                  <tr
                    key={bill._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-2 px-2 text-center text-sm text-gray-700 border-r">
                      {rowNumber}
                    </td>
                    <td className="py-2 px-2 text-sm text-gray-700 border-r">
                      {bill.studentId?.admissionNo || "N/A"}
                    </td>
                    <td className="py-2 px-2 text-sm text-gray-700 border-r font-medium uppercase">
                      {bill.studentId?.studentName || "N/A"}
                    </td>
                    <td className="py-2 px-2 text-sm text-gray-700 border-r">
                      {bill.studentId?.class ||
                        bill.studentId?.className ||
                        "N/A"}
                    </td>
                    <td className="py-2 px-2 text-sm text-gray-700 border-r">
                      {bill.academicYear || "N/A"}
                    </td>
                    <td className="py-2 px-2 text-sm text-gray-700 border-r">
                      {bill.invoiceNo || "N/A"}
                    </td>
                    <td className="py-2 px-2 text-sm text-gray-700 border-r font-semibold">
                      ₹{bill.totalAmount?.toLocaleString() || 0}
                    </td>
                    <td className="py-2 px-2 text-sm text-gray-700 border-r">
                      ₹{bill.remainingAmount?.toLocaleString() || 0}
                    </td>
                    <td className="py-2 px-2 text-sm text-gray-700 border-r">
                      {bill.modeOfPayment || "N/A"}
                    </td>
                    <td className="py-2 px-2 text-center text-sm text-gray-700 border-r">
                      {formattedDate}
                    </td>
                    <td className="py-2 px-2 text-center flex justify-center gap-3">
                      <button
                        onClick={() => handleViewClick(bill)}
                        className="flex items-center justify-center gap-1 text-[#4361ee] font-medium hover:underline text-sm"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        onClick={() => handleEditClick(bill)}
                        className="flex items-center justify-center gap-1 text-green-600 font-medium hover:underline text-sm"
                      >
                        <FaEdit /> Edit
                      </button>
                      {/* 
                                <button onClick={() => handleDeleteBill(bill._id)} className="ml-2 text-red-500">Delete</button> 
                                */}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" className="py-6 text-center text-gray-500">
                  No bills found matching your criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Section */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6 gap-2">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className={`w-8 h-8 flex items-center justify-center rounded border ${currentPage === 1 ? "border-gray-200 text-gray-300" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}
          >
            &lt;
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => paginate(i + 1)}
              className={`w-8 h-8 flex items-center justify-center rounded border ${currentPage === i + 1 ? "bg-[#16bab1] text-white border-[#16bab1]" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`w-8 h-8 flex items-center justify-center rounded border ${currentPage === totalPages ? "border-gray-200 text-gray-300" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}
          >
            &gt;
          </button>
        </div>
      )}
    </div>
  );
};

export default MiscBillList;